/**
 * The signed-in person and the organization they're working as, from Better
 * Auth. The provider renders its children only once it knows who is signed
 * in, so nothing downstream sees a loading state: `user` is a person or
 * `null`.
 */
import { useRouter } from "@tanstack/react-router";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { invariant } from "ts-invariant";

import { bearAuthClient } from "@/lib/bear-auth-client";

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  image: string | null;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  /** The ID people type to share a project with this organization. */
  slug: string;
}

export interface Session {
  user: SessionUser | null;
  /**
   * The organization the user is acting as, which new work is created under:
   * the active one, else the first membership. `null` for a user with no
   * organization yet.
   */
  organization: OrganizationSummary | null;
  /** Every organization the user belongs to. */
  organizations: OrganizationSummary[];
  switchOrganization(organizationId: string): Promise<void>;
  signIn(): void;
  signOut(): Promise<void>;
  openAccount(): void;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  invariant(session, "useSession must be used inside a SessionProvider");
  return session;
}

/** The signed-in user; throws when used on a signed-out screen. */
export function useSignedInUser(): SessionUser {
  const { user } = useSession();
  invariant(user, "useSignedInUser requires a signed-in session");
  return user;
}

/** Renders its children for a signed-in user; sends anyone else to sign in. */
export function RequireSignIn({ children }: { children: ReactNode }) {
  const { user, signIn } = useSession();
  useEffect(() => {
    if (!user) signIn();
  }, [user, signIn]);
  return user ? children : null;
}

/**
 * Whether `query` has answered since `key` (who is signed in) last changed.
 * Better Auth reports a refetch of `null` data as pending, so gating on
 * `isPending` alone unmounts the app on every refetch, and a remount's own
 * fetch refetches again.
 */
function useAnswered(query: { isPending: boolean }, key: string | null) {
  const [answeredFor, setAnsweredFor] = useState<string | null>();
  if (!query.isPending && answeredFor !== key) setAnsweredFor(key);
  return answeredFor === key || !query.isPending;
}

export function SessionProvider({
  loading,
  children,
}: {
  loading: ReactNode;
  children: ReactNode;
}) {
  const signedIn = bearAuthClient.useSession();
  const organizations = bearAuthClient.useListOrganizations();
  const router = useRouter();
  const user = signedIn.data?.user;
  const memberships = useMemo(
    () =>
      (organizations.data ?? []).map(({ id, name, slug }) => ({
        id,
        name,
        slug,
      })),
    [organizations.data],
  );
  const organization =
    memberships.find(
      (org) => org.id === signedIn.data?.session.activeOrganizationId,
    ) ??
    memberships[0] ??
    null;
  const session = useMemo<Session>(
    () => ({
      user: user
        ? {
            id: user.id,
            name: user.name,
            email: user.email,
            image: user.image ?? null,
          }
        : null,
      organization,
      organizations: memberships,
      switchOrganization: async (organizationId) => {
        const result = await bearAuthClient.organization.setActive({
          organizationId,
        });
        invariant(!result.error, result.error?.message);
        // Better Auth doesn't refresh memberships on accepting an invitation,
        // and the org just joined is the one being switched to.
        organizations.refetch();
      },
      signIn: () =>
        void router.navigate({
          to: "/sign-in",
          search: { redirect: router.state.location.href },
        }),
      signOut: async () => {
        await bearAuthClient.signOut();
        await router.navigate({ to: "/" });
      },
      openAccount: () => void router.navigate({ to: "/account" }),
    }),
    [user, organization, memberships, organizations.refetch, router],
  );
  const userId = user?.id ?? null;
  const sessionAnswered = useAnswered(signedIn, null);
  const organizationsAnswered = useAnswered(organizations, userId);
  if (!sessionAnswered || (user && !organizationsAnswered)) return loading;
  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
}
